/**
 * Périmètres de surfaces brûlées, depuis le service WFS d'EFFIS Copernicus.
 *
 * Appel DIRECT depuis le navigateur : le service envoie
 * `Access-Control-Allow-Origin: *`, donc aucune fonction serveur n'est
 * nécessaire, contrairement à FIRMS qui exige une clé. Cette couche fonctionne
 * donc sur n'importe quel hébergement, y compris statique.
 *
 * Ce que cette source apporte, et ce qu'elle n'apporte pas : elle donne de vrais
 * périmètres, dérivés d'imagerie, avec des centaines de sommets là où nos
 * empreintes satellite en comptent quelques dizaines. Mais elle décrit ce qui a
 * BRÛLÉ, pas ce qui brûle. Voir src/core/burnt.ts.
 */
import { parseBurntAreas } from '../core/burnt';
import {
  BURNT_DAYS_BACK,
  EFFIS_LAYER,
  EFFIS_WFS_URL,
  REQUEST_TIMEOUT_MS,
} from '../core/constants';
import { FRANCE_BOX } from '../core/france';
import type { BurntArea } from '../core/types';

/**
 * Filtre OGC combinant l'emprise et la date.
 *
 * Les deux doivent tenir dans le même filtre : WFS 1.0.0 interdit d'employer
 * `bbox` et `filter` dans la même requête.
 *
 * Et le filtre de date seul ne suffit pas. Sans emprise, le service tombe sur une
 * entité aux coordonnées hors des bornes terrestres, son écriture GeoJSON échoue,
 * et la réponse entière est perdue. L'emprise France l'écarte au passage.
 */
function ogcFilter(since: Date): string {
  const day = since.toISOString().slice(0, 10);
  return [
    '<Filter><And>',
    '<BBOX><PropertyName>msGeometry</PropertyName>',
    '<Box srsName="EPSG:4326"><coordinates>',
    `${FRANCE_BOX.west},${FRANCE_BOX.south} ${FRANCE_BOX.east},${FRANCE_BOX.north}`,
    '</coordinates></Box></BBOX>',
    '<PropertyIsGreaterThan><PropertyName>initialdate</PropertyName>',
    `<Literal>${day}</Literal>`,
    '</PropertyIsGreaterThan>',
    '</And></Filter>',
  ].join('');
}

function endpoint(now: number): string {
  const since = new Date(now - BURNT_DAYS_BACK * 86_400_000);
  const params = new URLSearchParams({
    service: 'WFS',
    version: '1.0.0',
    request: 'GetFeature',
    typename: EFFIS_LAYER,
    outputformat: 'geojson',
    filter: ogcFilter(since),
  });
  return `${EFFIS_WFS_URL}?${params.toString()}`;
}

/**
 * Isole le GeoJSON d'une réponse qui peut en contenir davantage.
 *
 * Quand une entité résiste à l'écriture, MapServer produit un document valide
 * PUIS y accole un rapport d'erreur XML. Le tout n'est plus du JSON. On coupe
 * donc à la première ligne d'en-tête recollée, ce qui préserve les entités déjà
 * écrites au lieu de tout perdre.
 */
function firstJsonDocument(body: string): string {
  const marker = body.indexOf('\nContent-Type:');
  return marker === -1 ? body : body.slice(0, marker);
}

/**
 * Interroge EFFIS.
 *
 * Renvoie un tableau vide sur toute difficulté, sans lever. Une couche de
 * contexte absente ne doit pas empêcher l'application de répondre à sa question,
 * et surtout pas faire croire à une panne : contrairement au balayage satellite,
 * l'absence de surfaces brûlées n'est pas une information trompeuse.
 */
export async function fetchBurntAreas(now: number): Promise<BurntArea[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(endpoint(now), {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return [];
    const body = await response.text();
    return parseBurntAreas(JSON.parse(firstJsonDocument(body)) as unknown, now);
  } catch {
    return [];
  } finally {
    clearTimeout(timer);
  }
}
